import { forwardRef } from "react";

// eslint-disable-next-line no-restricted-imports
import { Stack, ButtonProps, useTheme, CircularProgress, SxProps, Theme } from "@mui/material";
import ButtonBase from "@mui/material/ButtonBase";

import { NextLink } from "../../../components/NextLink/NextLink";
import { InvalidThemeErrorMessage } from "../InvalidThemeErrorMessage";

import { FormIcon, FormIconProps } from "@/forms-design-system/FormIcon";
import { mergeSx } from "@/helpers/mergeSx";
import { IconElement } from "@/icons/types";

export type InnerFormButtonProps = Omit<
  ButtonProps,
  "variant" | "slot" | "loading" | "size" | "disableRipple" | "startIcon" | "endIcon"
> & {
  variant?: "solid" | "tonal" | "outline" | "ghost";
  size?: "small" | "medium" | "large";
  isLoading?: boolean;
  fullWidth?: boolean;
  iconButton?: boolean;
  startIcon?: IconElement;
  endIcon?: IconElement;
  backgroundContent?: React.ReactNode;
  components?: {
    startIcon?: {
      size?: FormIconProps["size"];
      sx?: SxProps<Theme>;
    };
    endIcon?: {
      size?: FormIconProps["size"];
      sx?: SxProps<Theme>;
    };
  };
};

export const InnerFormButton = forwardRef<HTMLButtonElement, InnerFormButtonProps>(
  (
    {
      isLoading = false,
      disabled = false,
      children,
      size = "medium",
      variant = "solid",
      iconButton = false,
      startIcon,
      endIcon,
      fullWidth = false,
      backgroundContent,
      components,
      sx,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.global || !theme.palette.formsComponents?.button) {
      return <InvalidThemeErrorMessage />;
    }

    const styles: SxProps<Theme> = mergeSx(
      {
        borderRadius: theme.spacing(1),
        cursor: disabled ? undefined : "pointer",
        transition: "background-color 200ms ease-out, outline 100ms ease-out",
        outlineColor: "transparent",
        ...(variant === "solid" && {
          color: theme.palette.formsComponents.button.solid.color,
          backgroundColor: theme.palette.formsComponents.button.solid.backgroundColor,
          ["&:hover"]: {
            backgroundColor: theme.palette.formsComponents.button.solid.hoverBackgroundColor,
          },
          ["&:active, &:focus-visible"]: {
            backgroundColor: theme.palette.formsComponents.button.solid.backgroundColor,
          },
          ["&:focus-visible"]: {
            outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
          },
        }),
        ...(variant === "tonal" && {
          color: theme.palette.formsComponents.button.tonal.color,
          backgroundColor: theme.palette.formsComponents.button.tonal.backgroundColor,
          ["&:hover"]: {
            backgroundColor: theme.palette.formsComponents.button.tonal.hoverBackgroundColor,
          },
          ["&:active, &:focus-visible"]: {
            backgroundColor: theme.palette.formsComponents.button.tonal.backgroundColor,
            outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
          },
        }),
        ...(variant === "outline" && {
          color: theme.palette.formsComponents.button.outline.color,
          backgroundColor: theme.palette.formsComponents.button.outline.backgroundColor,
          border: "1px solid",
          borderColor: theme.palette.formsComponents.button.outline.borderColor,
          ["&:hover"]: {
            backgroundColor: theme.palette.formsComponents.button.outline.hoverBackgroundColor,
          },
          ["&:active, &:focus-visible"]: {
            backgroundColor: theme.palette.formsComponents.button.outline.backgroundColor,
            outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
          },
        }),
        ...(variant === "ghost" && {
          color: theme.palette.formsComponents.button.ghost.color,
          backgroundColor: theme.palette.formsComponents.button.ghost.backgroundColor,
          ["&:hover"]: {
            backgroundColor: theme.palette.formsComponents.button.ghost.hoverBackgroundColor,
          },
          ["&:active, &:focus-visible"]: {
            backgroundColor: theme.palette.formsComponents.button.ghost.backgroundColor,
            outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
          },
        }),
        ...(size === "large" && {
          minHeight: "44px",
          padding: theme.spacing(1, 2),
          ...theme.typography.button1,
        }),
        ...(size === "medium" && {
          minHeight: "36px",
          padding: theme.spacing(1, 2),
          ...theme.typography.button2,
        }),
        ...(size === "small" && {
          minHeight: "30px",
          padding: theme.spacing(0.5, 2),
          ...theme.typography.button3,
        }),
        ...(iconButton && {
          padding: theme.spacing(0.5),
          width: size === "large" ? "44px" : size === "medium" ? "36px" : "30px",
          height: size === "large" ? "44px" : size === "medium" ? "36px" : "30px",
        }),
        ...((disabled || isLoading) && { opacity: 0.4 }),
        ...(fullWidth && { width: "100%" }),
      },
      sx
    );

    const iconSize =
      size === "large" && iconButton
        ? "large"
        : size === "large" || (size === "medium" && iconButton)
          ? "medium"
          : "small";

    return (
      <ButtonBase ref={ref} {...rest} disableRipple disabled={isLoading || disabled} sx={styles}>
        {isLoading && (
          <CircularProgress
            size={theme.icon.size.default[size === "large" ? "small" : "extraSmall"]}
            sx={{ position: "absolute", color: "inherit" }}
          />
        )}
        <Stack
          display="inline-flex"
          direction="row"
          gap={0.5}
          alignItems="center"
          justifyContent="center"
          sx={{
            position: "relative",
            zIndex: 1,
            ...(isLoading && { opacity: 0 }),
          }}
        >
          {startIcon && (
            <FormIcon
              size={components?.startIcon?.size ?? iconSize}
              sx={mergeSx({ flexShrink: 0 }, components?.startIcon?.sx)}
            >
              {startIcon}
            </FormIcon>
          )}
          {!iconButton && children}
          {endIcon && !iconButton && (
            <FormIcon
              size={components?.endIcon?.size ?? iconSize}
              sx={mergeSx({ flexShrink: 0 }, components?.endIcon?.sx)}
            >
              {endIcon}
            </FormIcon>
          )}
        </Stack>
        {backgroundContent}
      </ButtonBase>
    );
  }
);

InnerFormButton.displayName = "InnerFormButton";

export type FormButtonProps = InnerFormButtonProps & {
  href?: string;
  target?: string;
};

export const FormButton = forwardRef<HTMLButtonElement, FormButtonProps>((props, ref) => {
  const { href, target, ...rest } = props;
  if (href && target !== "_blank") {
    return (
      <NextLink href={href} target={target} locale={false}>
        <InnerFormButton {...rest} ref={ref} />
      </NextLink>
    );
  } else {
    return <InnerFormButton href={href} {...rest} ref={ref} />;
  }
});

FormButton.displayName = "FormButton";
